import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, Spinner } from "react-bootstrap";
import axios from "../api/axios";

const formatNumber = (value) => {
  const num = Number(value || 0);
  return num.toLocaleString("en-IN");
};

const getPercentage = (part, total) => {
  const p = Number(part || 0);
  const t = Number(total || 0);

  if (!t) return "0.0";

  return ((p / t) * 100).toFixed(1);
};

const MiniKpiCard = ({
  title,
  value,
  icon,
  accent,
  bg,
  shape,
  loading,
  metric,
}) => (
  <div
    className="position-relative overflow-hidden h-100"
    style={{
      borderRadius: 18,
      background: bg,
      minHeight: 118,
      padding: "18px 20px",
      border: "1px solid rgba(15,23,42,0.04)",
      boxShadow: "0 8px 22px rgba(15, 23, 42, 0.05)",
    }}
  >
    <div
      style={{
        position: "absolute",
        right: -18,
        bottom: -26,
        width: 112,
        height: 112,
        borderRadius: shape === "circle" ? "50%" : 28,
        background: accent,
        opacity: 0.18,
        transform: shape === "rotate" ? "rotate(18deg)" : "none",
      }}
    />

    <div
      style={{
        position: "absolute",
        right: 20,
        top: 18,
        width: 42,
        height: 42,
        borderRadius: 14,
        background: "#ffffffaa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: accent,
        fontSize: 21,
        boxShadow: "0 8px 18px rgba(15,23,42,0.06)",
      }}
    >
      <i className={`bi ${icon}`} />
    </div>

    <div style={{ position: "relative", zIndex: 2, paddingRight: 48 }}>
      <div
        style={{
          color: accent,
          fontSize: 15,
          fontWeight: 900,
          marginBottom: 12,
        }}
      >
        {title}
      </div>

      {loading ? (
        <Spinner animation="border" size="sm" style={{ color: accent }} />
      ) : (
        <>
          <div
            style={{
              color: "#111827",
              fontSize: 27,
              fontWeight: 950,
              letterSpacing: "-0.7px",
              lineHeight: 1,
            }}
          >
            {formatNumber(value)}
          </div>

          {metric && (
            <div
              style={{
                marginTop: 10,
                color: accent,
                fontSize: 12,
                fontWeight: 900,
                background: "#ffffffb8",
                display: "inline-flex",
                padding: "5px 9px",
                borderRadius: 999,
              }}
            >
              {metric}
            </div>
          )}
        </>
      )}
    </div>
  </div>
);

const SmallStat = ({ title, value, icon, accent, bg, metric }) => (
  <div
    className="d-flex align-items-center justify-content-between h-100"
    style={{
      padding: "15px 16px",
      borderRadius: 16,
      background: bg,
      border: "1px solid rgba(15,23,42,0.04)",
      minHeight: 78,
    }}
  >
    <div className="d-flex align-items-center">
      <div
        className="d-flex align-items-center justify-content-center me-3"
        style={{
          width: 38,
          height: 38,
          borderRadius: 13,
          background: "#ffffffc7",
          color: accent,
          fontSize: 17,
          boxShadow: "0 6px 14px rgba(15,23,42,0.05)",
          flexShrink: 0,
        }}
      >
        <i className={`bi ${icon}`} />
      </div>

      <div
        style={{
          color: "#4B5563",
          fontSize: 13,
          fontWeight: 800,
          lineHeight: 1.2,
        }}
      >
        {title}
      </div>
    </div>

    <div style={{ textAlign: "right", marginLeft: 10 }}>
      <div
        style={{
          color: "#111827",
          fontSize: 19,
          fontWeight: 950,
          lineHeight: 1,
        }}
      >
        {formatNumber(value)}
      </div>

      {metric && (
        <div
          style={{
            color: accent,
            fontSize: 11,
            fontWeight: 900,
            marginTop: 5,
            whiteSpace: "nowrap",
          }}
        >
          {metric}
        </div>
      )}
    </div>
  </div>
);

const AllTimeKPI = () => {
  const token = localStorage.getItem("authToken");

  const [ordersData, setOrdersData] = useState({
    totalOrders: 0,
    totalCancelledOrders: 0,
    totalOrdersWithCoupons: 0,
    totalOrdersWithoutCoupons: 0,
  });

  const [usersData, setUsersData] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchAllTimeKPIData = useCallback(async () => {
    try {
      setLoading(true);

      const [ordersResponse, usersResponse] = await Promise.all([
        axios.get("dashboard/getOrdersStats", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get("dashboard/getUsersDataStats", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (ordersResponse?.data?.status === true) {
        setOrdersData(ordersResponse?.data?.data?.data || {});
      }

      if (usersResponse?.data?.data) {
        setUsersData(usersResponse.data.data || {});
      }
    } catch (error) {
      console.error("Error fetching all time KPI data:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAllTimeKPIData();
  }, [fetchAllTimeKPIData]);

  const getUserCount = (key) => usersData?.[key]?.[0]?.count || 0;

  const totalOrders = Number(ordersData?.totalOrders || 0);
  const totalCancelledOrders = Number(ordersData?.totalCancelledOrders || 0);
  const totalOrdersWithCoupons = Number(ordersData?.totalOrdersWithCoupons || 0);
  const totalOrdersWithoutCoupons = Number(
    ordersData?.totalOrdersWithoutCoupons || 0
  );

  const totalAppUsers = Number(getUserCount("totalNumberOfAppUsers"));
  const totalRepeatingUsers = Number(getUserCount("totalRepeatingUsers"));
  const totalReferredUsers = Number(getUserCount("totalReferredUsers"));
  const totalUsersWithoutOrders = Number(getUserCount("totalUsersWithoutOrders"));
  const totalDeletedUsers = Number(getUserCount("totalNumberOfDeletedUsers"));

  const activeUsers = Math.max(totalAppUsers - totalDeletedUsers, 0);
  const successfulOrders = Math.max(totalOrders - totalCancelledOrders, 0);

  const primaryKpis = [
    {
      title: "Total Orders",
      value: totalOrders,
      icon: "bi-bag-check-fill",
      accent: "#E94E1B",
      bg: "linear-gradient(135deg, #FFF4EC 0%, #FFEBDD 100%)",
      shape: "circle",
      metric: `${getPercentage(successfulOrders, totalOrders)}% successful`,
    },
    {
      title: "App Users",
      value: totalAppUsers,
      icon: "bi-person-fill",
      accent: "#1677F2",
      bg: "linear-gradient(135deg, #EEF7FF 0%, #E3F1FF 100%)",
      shape: "circle",
      metric: `${getPercentage(activeUsers, totalAppUsers)}% active users`,
    },
    {
      title: "Repeating Users",
      value: totalRepeatingUsers,
      icon: "bi-arrow-repeat",
      accent: "#D91D8E",
      bg: "linear-gradient(135deg, #FFF1FA 0%, #FCE7F3 100%)",
      shape: "rotate",
      metric: `${getPercentage(totalRepeatingUsers, totalAppUsers)}% repeat rate`,
    },
  ];

  const secondaryKpis = [
    {
      title: "Cancelled Orders",
      value: totalCancelledOrders,
      icon: "bi-x-circle-fill",
      accent: "#EF4444",
      bg: "#FFF5F5",
      metric: `${getPercentage(totalCancelledOrders, totalOrders)}% of orders`,
    },
    {
      title: "Orders With Coupons",
      value: totalOrdersWithCoupons,
      icon: "bi-ticket-perforated-fill",
      accent: "#7C3AED",
      bg: "#F5F0FF",
      metric: `${getPercentage(totalOrdersWithCoupons, totalOrders)}% coupon usage`,
    },
    {
      title: "Orders W/O Coupons",
      value: totalOrdersWithoutCoupons,
      icon: "bi-bag-fill",
      accent: "#F59E0B",
      bg: "#FFF8E8",
      metric: `${getPercentage(
        totalOrdersWithoutCoupons,
        totalOrders
      )}% direct orders`,
    },
    {
      title: "Referred Users",
      value: totalReferredUsers,
      icon: "bi-people-fill",
      accent: "#0EA5E9",
      bg: "#EEF9FF",
      metric: `${getPercentage(totalReferredUsers, totalAppUsers)}% of users`,
    },
    {
      title: "Users W/O Orders",
      value: totalUsersWithoutOrders,
      icon: "bi-cart-x-fill",
      accent: "#10B981",
      bg: "#ECFDF5",
      metric: `${getPercentage(totalUsersWithoutOrders, totalAppUsers)}% inactive`,
    },
    {
      title: "Deleted Users",
      value: totalDeletedUsers,
      icon: "bi-trash3-fill",
      accent: "#64748B",
      bg: "#F8FAFC",
      metric: `${getPercentage(totalDeletedUsers, totalAppUsers)}% deleted`,
    },
  ];

  return (
    <Container fluid className="p-0">
      <Card
        className="border-0 overflow-hidden"
        style={{
          borderRadius: 24,
          background: "#FFFFFF",
          boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)",
        }}
      >
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <div className="d-flex align-items-center mb-1">
                <h5
                  className="mb-0"
                  style={{
                    fontWeight: 950,
                    color: "#111827",
                    letterSpacing: "-0.4px",
                  }}
                >
                  Business Highlights
                </h5>
              </div>

              <div
                style={{
                  fontSize: 13,
                  color: "#6B7280",
                  fontWeight: 600,
                }}
              >
                Overall customer and order summary till date
              </div>
            </div>
          </div>

          <Row className="g-3 mb-3">
            {primaryKpis.map((item) => (
              <Col lg={4} md={4} sm={12} key={item.title}>
                <MiniKpiCard {...item} loading={loading} />
              </Col>
            ))}
          </Row>

          {loading ? (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ minHeight: 120 }}
            >
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <Row className="g-3">
              {secondaryKpis.map((item) => (
                <Col xl={4} md={6} xs={12} key={item.title}>
                  <SmallStat {...item} />
                </Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AllTimeKPI;